// LogoUploader.js - Component for uploading and validating invoice logos.
// Validates file size (max 5MB) and type; displays a styled square upload area with icon and placeholder.
// Shows preview with a hover-to-remove "X" overlay for cleaner UX. Dimensions validation removed for flexibility.
// Fixed: Made upload area square and responsive; fixed re-upload issue by resetting file input on remove.
// Fixed: Added theme prop for dark mode support; conditionally applies background colors and hover effects.

import React, { useState, useRef } from 'react';
import Form from 'react-bootstrap/Form';
import Alert from 'react-bootstrap/Alert';
import { BiUpload, BiX } from 'react-icons/bi';  // Icons for upload and remove.
import { useTranslation } from 'react-i18next';

const LogoUploader = ({ logo, setLogo, theme }) => {  // Added: theme prop for conditional styling.
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [isHovered, setIsHovered] = useState(false);  // State to track hover for remove overlay.
  const fileInputRef = useRef(null);  // Ref to trigger hidden file input.
  const { t } = useTranslation();

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setError('');
    setUploading(true);

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Invalid file type. Please upload a PNG, JPG, JPEG, GIF, or WebP image.');
      setUploading(false);
      return;
    }

    // Validate file size (5MB max)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      setError('File size exceeds 5MB. Please choose a smaller image.');
      setUploading(false);
      return;
    }

    // Convert to base64 and set logo (no dimension checks)
    const reader = new FileReader();
    reader.onload = () => {
      setLogo(reader.result); // Base64 string
      setUploading(false);
    };
    reader.onerror = () => {
      setError('Failed to load image. Please try again.');
      setUploading(false);
    };
    reader.readAsDataURL(file);
  };

  const handleUploadClick = () => {
    if (!logo && !uploading) {
      fileInputRef.current.click();  // Trigger hidden file input.
    }
  };

  const handleRemoveLogo = () => {
    setLogo('');  // Clear the logo.
    setIsHovered(false);  // Reset hover state.
    if (fileInputRef.current) {
      fileInputRef.current.value = '';  // Reset file input to allow re-uploading the same file.
    }
  };

  // Theme-aware background colors for upload area
  const uploadBgClass = theme === 'dark' ? 'bg-dark' : 'bg-white';
  const hoverBgColor = theme === 'dark' ? '#495057' : '#f8f9fa';  // Dark gray for dark mode, light gray for light mode.

  return (
    <div className="mb-3">
      <Form.Control
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        style={{ display: 'none' }}  // Hidden input, triggered by the styled box.
      />
      {!logo ? (
        <div
          className={`border border-2 border-dashed rounded p-4 text-center d-flex flex-column justify-content-center align-items-center ${uploadBgClass} ${!uploading ? 'cursor-pointer' : ''}`}
          style={{
            width: '120px',
            height: '120px',
            margin: '0 auto',  // Center the square box.
            cursor: uploading ? 'not-allowed' : 'pointer'
          }}
          onClick={handleUploadClick}
          onMouseEnter={(e) => !uploading && (e.target.style.backgroundColor = hoverBgColor)}
          onMouseLeave={(e) => !uploading && (e.target.style.backgroundColor = theme === 'dark' ? '#212529' : 'white')}  // Reset to theme-appropriate color.
        >
          <BiUpload size={40} className="text-muted mb-2" />
          <small className="text-muted text-center">{t('uploadLogo')}<br />PNG, JPG, GIF, WebP<br />(max 5MB)</small>
        </div>
      ) : (
        <div
          className="position-relative d-inline-block mt-2"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onClick={handleRemoveLogo}
          style={{ cursor: 'pointer' }}
        >
          <img
            src={logo}
            alt="Invoice Logo"
            style={{ maxWidth: '175px', maxHeight: '128px', display: 'block' }}
          />
          {isHovered && (
            <div
              className="position-absolute top-0 end-0 bg-dark bg-opacity-75 rounded-circle d-flex align-items-center justify-content-center"
              style={{
                width: '30px',
                height: '30px',
                transform: 'translate(50%, -50%)',
              }}
            >
              <BiX size={20} className="text-white" />
            </div>
          )}
        </div>
      )}
      {error && <Alert variant="danger" className="mt-2">{error}</Alert>}
      {uploading && <p className="text-muted">{t('uploading')}</p>}
    </div>
  );
};

export default LogoUploader;